import { getCountryStates } from '@ikea-customer-self-serve/contact-form';
import { ERRORS } from '../errors';
import {
  type LocationServiceResponse,
  type PostalCodeLocation,
  type AddressComponents,
  type PostalCodeLookup,
  type GeoCodes,
} from './model';

/* global CSS_CONFIG */
const {
  country,
  getStates: { useStateNameAbbreviation, ingkaLocationServiceApi, apiSrc },
  defaultLanguage,
  language,
  services: { getServicesUsingStateCode },
} = CSS_CONFIG;

class State {
  private getFallbackLocations(): PostalCodeLocation[] {
    // Populate with fallback locations for countries requiring a state code
    // in get services request.
    return getServicesUsingStateCode
      ? getCountryStates(country).map((state) => {
          return { state };
        })
      : [];
  }

  private async getStateByLocationServiceAPI(
    postalCode: string,
  ): Promise<PostalCodeLookup> {
    try {
      const response = await fetch(
        `${ingkaLocationServiceApi}/locationservice/${country}/${postalCode}`,
      );
      if (!response.ok) {
        if (response.status === ERRORS.BAD_REQUEST) {
          return { locations: [], valid: false };
        }
        return { locations: this.getFallbackLocations(), valid: true };
      }
      const result: LocationServiceResponse = await response.json();
      let locations: PostalCodeLocation[] = [];

      if (country === 'au') {
        const { additionalAttributes } = result;
        locations = (additionalAttributes?.stateSuburbRelation || []).map(
          (stateSuburb) => {
            return { state: stateSuburb.state, city: stateSuburb.city };
          },
        );
        return { locations, valid: true };
      }

      const { additionalAttributes } = result;
      locations = (additionalAttributes?.stateInfo || []).map((info) => {
        const state = useStateNameAbbreviation
          ? info.stateCode
          : info.stateName || info.stateCode;
        return { state };
      });

      // Invalid postal code case 1.
      // US returns status 200 and validZipCode set to false for invalid postal code.
      const { validZipCode } = result;
      const valid = validZipCode !== undefined ? validZipCode : true;

      return { locations, valid };
    } catch (err) {
      return { locations: this.getFallbackLocations(), valid: true };
    }
  }

  private filterOutIrrelevantMarketsGeoCodes = (data: GeoCodes) => {
    for (const result of data.results) {
      for (const component of result.address_components) {
        const isItCountryComponent = component.types.includes('country');
        if (
          isItCountryComponent &&
          component.short_name.toLowerCase() === country.toLowerCase()
        ) {
          return result;
        }
      }
    }
  };

  private async getStateByGoogleGeocodeAPI(
    postalCode: string,
  ): Promise<PostalCodeLookup> {
    try {
      const url = `${CSS_CONFIG.backendUrl.bokaCustomerApi}/geocode/${postalCode}`;
      const headers = {
        'country-language': `${country}-${defaultLanguage || language}`,
      };
      const response = await fetch(url, {
        headers,
      });
      if (!response.ok) {
        return { locations: this.getFallbackLocations(), valid: true };
      }
      const data: GeoCodes = await response.json();
      const geocode = this.filterOutIrrelevantMarketsGeoCodes(data);

      if (!geocode) {
        return { locations: this.getFallbackLocations(), valid: true };
      }
      const addressComponents = geocode.address_components;
      const [localArray] = (addressComponents || []).filter(
        (item: AddressComponents) =>
          item.types.some((type: string) => type === 'locality'),
      );
      const [stateArray] = (addressComponents || []).filter(
        (item: AddressComponents) =>
          item.types.some(
            (type: string) => type === 'administrative_area_level_1',
          ),
      );

      const city = localArray?.long_name;
      const state = useStateNameAbbreviation
        ? stateArray.short_name
        : stateArray.long_name;

      return state
        ? { locations: [{ city, state }], valid: true }
        : { locations: this.getFallbackLocations(), valid: true };
    } catch (err) {
      return { locations: this.getFallbackLocations(), valid: true };
    }
  }

  public async getPostalCodeLocations(
    postalCode: string,
  ): Promise<PostalCodeLookup> {
    if (apiSrc === 'GOOGLE') {
      return this.getStateByGoogleGeocodeAPI(postalCode);
    }

    if (apiSrc === 'INGKA_LOCATION') {
      return this.getStateByLocationServiceAPI(postalCode);
    }

    return Promise.resolve({ locations: [], valid: true });
  }
}

export default State;
